import { useEffect, useRef, useState, type ComponentType, type MouseEvent as ReactMouseEvent, type SVGProps } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  IconBed,
  IconBillCheck,
  IconChart,
  IconClock,
  IconEdit,
  IconIdCard,
  IconPill,
  IconPrinter,
  IconPulse,
  IconRefreshCw,
  IconStethoscope,
  IconWallet,
} from '../../components/layout/icons';
import { BillActionGrid, BillActionHoverTile, BillActionTile } from '../../components/ui/BillActionTile';
import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { openAuthenticatedRoute } from '../../lib/auth';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useToast } from '../../lib/toast';
import type { BillableAdmission } from '../ipd-daycare/billable-admission';
import { IpdAllBillList } from '../ipd-daycare/IpdAllBillList';
import { IpdAllReceiptList } from '../ipd-daycare/IpdAllReceiptList';
import { IpdAllRefundList } from '../ipd-daycare/IpdAllRefundList';
import { IpdBillDetailsView } from '../ipd-daycare/IpdBillDetailsView';
import { IpdNewReceiptForm } from '../ipd-daycare/IpdNewReceiptForm';
import { useIpdBills } from '../ipd-daycare/ipd-daycare.queries';
import { currency } from '../ipd-daycare/ipd-daycare.utils';
import { DialysisBedTransferPanel } from './DialysisBedTransferPanel';
import { DialysisCreateBillForm } from './DialysisCreateBillForm';
import { DialysisEditAdmissionForm } from './DialysisEditAdmissionForm';
import { DialysisEditBillForm } from './DialysisEditBillForm';
import { DialysisPrescriptionForm } from './DialysisPrescriptionForm';
import { useDialysisAdmission } from './dialysis.queries';
import { formatDialysisDateTime, formatDialysisTime } from './dialysis.utils';

type DetailTab = 'admission-details' | 'bill-details' | 'create-bill' | 'edit-bill' | 'all-bill' | 'all-receipt' | 'all-refund' | 'new-receipt' | 'print-bill' | 'create-prescription' | 'prescription' | 'edit-admission' | 'bed-transfer';

const blankTabs: Array<{ key: Exclude<DetailTab, 'admission-details' | 'bill-details' | 'create-bill' | 'edit-bill' | 'all-bill' | 'all-receipt' | 'all-refund' | 'new-receipt' | 'create-prescription' | 'prescription' | 'edit-admission' | 'bed-transfer'>; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; soon: boolean; message: string }> = [
  { key: 'print-bill', label: 'Print Bill', icon: IconPrinter, soon: false, message: 'This section is not available here yet. Use the Print button at the top of the page to print this admission summary.' },
];

const billTiles: Array<{ key: 'bill-details' | 'create-bill' | 'edit-bill' | 'all-bill' | 'all-receipt' | 'all-refund' | 'new-receipt'; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }> = [
  { key: 'bill-details', label: 'Bill Details', icon: IconWallet },
  { key: 'create-bill', label: 'Create New Bill', icon: IconPulse },
  { key: 'edit-bill', label: 'Edit Bill', icon: IconEdit },
  { key: 'new-receipt', label: 'New Receipt', icon: IconClock },
  { key: 'all-bill', label: 'All Bill', icon: IconChart },
  { key: 'all-receipt', label: 'All Receipt', icon: IconChart },
  { key: 'all-refund', label: 'All Refund', icon: IconRefreshCw },
];

export const DialysisDetailPage = () => {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState<DetailTab>('admission-details');
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [tileMenu, setTileMenu] = useState<{ top: number; left: number } | null>(null);
  const { showToast } = useToast();
  const tileMenuRef = useRef<HTMLDivElement>(null);
  const query = useDialysisAdmission(admissionId);
  const billsQuery = useIpdBills(admissionId);
  useEffect(() => { if (query.data && params.get('print') === '1') window.setTimeout(() => window.print(), 250); }, [params, query.data]);
  useEffect(() => {
    if (!tileMenu) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!tileMenuRef.current?.contains(event.target as Node)) setTileMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setTileMenu(null); };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [tileMenu]);
  if (query.isLoading) return <DetailSkeleton label="Loading admission details" />;
  if (query.isError || !query.data) return <ErrorState title="Dialysis admission not found" message="The admission could not be loaded." />;
  const row = query.data;
  const address = [row.patientSnapshot.address, row.patientSnapshot.district, row.patientSnapshot.state, row.patientSnapshot.pinCode].filter(Boolean).join(', ') || '-';
  const statusLabel = row.status === 'admitted' ? 'Admitted' : 'Discharged';
  const activeBlankTab = blankTabs.find((tab) => tab.key === activeTab);
  const bills = billsQuery.data ?? [];
  const latestBillId = bills[0]?.id ?? null;
  const effectiveBillId = selectedBillId ?? latestBillId;
  const activeBills = bills.filter((bill) => bill.status !== 'cancelled');
  const totalDue = activeBills.reduce((sum, bill) => sum + bill.dueAmount, 0);
  const totalCredit = activeBills.reduce((sum, bill) => sum + bill.creditAmount, 0);
  const lastBillNumber = bills[0]?.billNumber ?? null;
  const billableAdmission: BillableAdmission = {
    id: row.id,
    patient: row.patient,
    patientSnapshot: { name: row.patientSnapshot.name, uhid: row.patientSnapshot.uhid },
    bedCategory: row.bedCategory,
    bed: row.bed,
    admissionDate: row.admissionDate,
    status: row.status,
    admissionLabel: `Dialysis No ${row.dialysisNumber}`,
  };

  const goToTab = (tab: DetailTab) => setActiveTab(tab);
  const viewBill = (billId: string) => { setSelectedBillId(billId); setActiveTab('bill-details'); };
  const openTileMenu = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTileMenu((current) => current ? null : { top: rect.bottom + 6, left: rect.left });
  };
  const openAdmissionFormPrint = (withHeader: boolean) => {
    setTileMenu(null);
    openAuthenticatedRoute(`/dialysis/${row.id}/print-admission-form?header=${withHeader ? '1' : '0'}`);
  };
  const printBill = () => {
    if (!effectiveBillId) { showToast('Create a bill for this dialysis admission before printing.', 'warning'); return; }
    openAuthenticatedRoute(`/dialysis/${row.id}/print-bill/${effectiveBillId}?header=1`);
  };
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) void navigate(-1);
    else void navigate('/dialysis');
  };

  return (
    <div className="page-stack opd-page ipd-daycare-page opd-bill-page">
      <div className="page-heading opd-print-hidden">
        <div><p className="eyebrow">Dialysis</p><h1>Dialysis Admission Details</h1></div>
        <div className="opd-heading-actions">
          <button className="button button-secondary" type="button" onClick={goBack}>Back</button>
        </div>
      </div>

      <div className="opd-bill-layout">
        <aside className="content-card opd-bill-sidebar">
          <div className="opd-bill-patient-box">
            <p><strong>Patient Name : </strong>{row.patientSnapshot.name}</p>
            <p><strong>UHID : </strong>{row.patientSnapshot.uhid ?? '-'}</p>
            <p><strong>Sex : </strong>{row.patientSnapshot.gender} || <strong>Age : </strong>{row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</p>
            <p><strong>Mobile : </strong>{row.patientSnapshot.phone ?? '-'}</p>
            <p><strong>Address : </strong>{address}</p>
            <p><strong>Under Doctor : </strong>{doctorNameOrDash(row.underDoctor?.name)}</p>
            <p><strong>Department : </strong>{row.underDoctor?.department ?? '-'}</p>
            <p><strong>Dialysis Machine : </strong>{row.dialysisMachine?.name ?? '-'}</p>
            <p><strong>Discharge Status : </strong>{statusLabel}</p>
            <p><strong>Last Bill No : </strong>{lastBillNumber ?? '-'}</p>
            {totalCredit > 0 ? <p><strong>Credit Amount : </strong><span className="opd-bill-patient-credit">{currency(totalCredit)}</span></p> : null}
            {totalDue > 0 ? <p><strong>Due : </strong><span className="opd-bill-patient-due">{currency(totalDue)}</span></p> : null}
          </div>
          <BillActionGrid>
            <BillActionTile icon={IconIdCard} label="Patient Profile" to={`/patients/${row.patient.id}`} />
            <BillActionTile icon={IconBillCheck} label="Admission Details" active={activeTab === 'admission-details'} onClick={() => setActiveTab('admission-details')} />
            <BillActionTile icon={IconEdit} label="Edit Admission" active={activeTab === 'edit-admission'} onClick={() => goToTab('edit-admission')} />
            <BillActionTile icon={IconPrinter} label="Adm Form" active={!!tileMenu} onClick={openTileMenu} />
            {billTiles.map((tab) => (
              <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key} onClick={() => goToTab(tab.key)} />
            ))}
            {blankTabs.map((tab) => (
              tab.key === 'print-bill' ? (
                <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} onClick={printBill} />
              ) : (
                <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key} soon={tab.soon} onClick={() => setActiveTab(tab.key)} />
              )
            ))}
            <BillActionTile icon={IconPill} label="Create E-Prescription" active={activeTab === 'create-prescription'} onClick={() => goToTab('create-prescription')} />
            <BillActionHoverTile icon={IconStethoscope} label="Prescription" items={[
              { label: 'Prescription without letterhead', onClick: () => openAuthenticatedRoute(`/dialysis/${row.id}/print-prescription?header=0`) },
              { label: 'Prescription with letterhead', onClick: () => openAuthenticatedRoute(`/dialysis/${row.id}/print-prescription?header=1`) },
            ]} />
            <BillActionTile icon={IconBed} label="Bed Transfer" active={activeTab === 'bed-transfer'} onClick={() => goToTab('bed-transfer')} />
          </BillActionGrid>
        </aside>

        <section className="content-card opd-bill">
          {activeTab === 'bill-details' ? (
            <IpdBillDetailsView admission={billableAdmission} selectedBillId={effectiveBillId} onCreateBill={() => goToTab('create-bill')} />
          ) : activeTab === 'create-bill' ? (
            <DialysisCreateBillForm admission={row} onCreated={(billId) => { setSelectedBillId(billId); goToTab('bill-details'); }} />
          ) : activeTab === 'edit-bill' ? (
            effectiveBillId ? (
              <DialysisEditBillForm admission={row} billId={effectiveBillId} onCancelled={() => goToTab('bill-details')} />
            ) : (
              <EmptyState title="No bills yet" message="Create a bill for this admission before editing one." action={{ label: 'Create New Bill', onClick: () => goToTab('create-bill') }} />
            )
          ) : activeTab === 'all-bill' ? (
            <IpdAllBillList admission={billableAdmission} onViewBill={viewBill} />
          ) : activeTab === 'all-receipt' ? (
            <IpdAllReceiptList admission={billableAdmission} printPath={`/dialysis/${row.id}/print-receipt`} />
          ) : activeTab === 'all-refund' ? (
            <IpdAllRefundList admission={billableAdmission} />
          ) : activeTab === 'new-receipt' ? (
            <IpdNewReceiptForm admission={billableAdmission} onRecorded={(billId) => viewBill(billId)} />
          ) : activeTab === 'edit-admission' ? (
            <DialysisEditAdmissionForm admission={row} onSaved={() => goToTab('admission-details')} />
          ) : activeTab === 'create-prescription' ? (
            <DialysisPrescriptionForm admission={row} />
          ) : activeTab === 'bed-transfer' ? (
            <DialysisBedTransferPanel admission={row} />
          ) : activeBlankTab ? (
            <EmptyState
              title={`${activeBlankTab.label}${activeBlankTab.soon ? ' — Coming soon' : ''}`}
              message={activeBlankTab.message}
              action={{ label: 'Back to Admission Details', onClick: () => setActiveTab('admission-details') }}
            />
          ) : (
          <>
          <header className="opd-bill-banner">
            <div className="opd-bill-banner-row">
              <div className="opd-bill-banner-fields">
                <span>Dialysis No : {row.dialysisNumber}</span>
                <span>Admission Date : {formatDialysisDateTime(row.admissionDate)}</span>
              </div>
              <span className={`opd-badge ${row.status === 'admitted' ? 'active' : 'inactive'}`}>{statusLabel}</span>
            </div>
            <p>Registered By - {row.generatedBy.name}</p>
          </header>

          <section className="opd-bill-subsection">
            <h3>Patient Details</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Patient</dt><dd>{row.patientSnapshot.name}</dd></div>
              <div><dt>UHID</dt><dd>{row.patientSnapshot.uhid ?? '-'}</dd></div>
              <div><dt>Mobile</dt><dd>{row.patientSnapshot.phone ?? '-'}</dd></div>
              <div><dt>Gender / Age</dt><dd>{row.patientSnapshot.gender}, {row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</dd></div>
              <div><dt>Marital Status</dt><dd>{row.patientSnapshot.maritalStatus ?? '-'}</dd></div>
              <div><dt>Guardian Name</dt><dd>{row.patientSnapshot.guardianName ?? '-'}</dd></div>
              <div><dt>Relation</dt><dd>{row.patientSnapshot.relation ?? '-'}</dd></div>
              <div><dt>Guardian Contact No</dt><dd>{row.patientSnapshot.guardianContactNo ?? '-'}</dd></div>
              <div><dt>Aadhaar Card</dt><dd>{row.patientSnapshot.aadhaar ?? '-'}</dd></div>
              <div className="wide"><dt>Address</dt><dd>{address}</dd></div>
            </dl>
          </section>

          <section className="opd-bill-subsection">
            <h3>Admission Information</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Admission Date</dt><dd>{formatDialysisDateTime(row.admissionDate)}</dd></div>
              <div><dt>Patient Type</dt><dd>{row.insuranceType}</dd></div>
              <div><dt>Insurance No.</dt><dd>{row.insuranceNumber ?? '-'}</dd></div>
              <div><dt>Under Doctor</dt><dd>{doctorNameOrDash(row.underDoctor?.name)}</dd></div>
              <div><dt>Department</dt><dd>{row.underDoctor?.department ?? '-'}</dd></div>
              <div><dt>Dialysis Machine</dt><dd>{row.dialysisMachine.name}</dd></div>
              <div><dt>Start Time</dt><dd>{formatDialysisTime(row.startTime)}</dd></div>
              <div><dt>End Time</dt><dd>{formatDialysisTime(row.endTime)}</dd></div>
              <div><dt>Bed Category</dt><dd>{row.bedCategory.name}</dd></div>
              <div><dt>Bed</dt><dd>{row.bed.name}</dd></div>
              <div><dt>Referred By</dt><dd>{row.referredBy.name}</dd></div>
            </dl>
          </section>

          <section className="opd-bill-subsection">
            <h3>Responsible Person Details</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Name</dt><dd>{row.responsiblePerson.name ?? '-'}</dd></div>
              <div><dt>Relation With Patient</dt><dd>{row.responsiblePerson.relation ?? '-'}</dd></div>
              <div><dt>Phone No</dt><dd>{row.responsiblePerson.phone ?? '-'}</dd></div>
              <div><dt>Age</dt><dd>{row.responsiblePerson.age ?? '-'}</dd></div>
              <div className="wide"><dt>Address</dt><dd>{row.responsiblePerson.address ?? '-'}</dd></div>
            </dl>
          </section>
          </>
          )}
        </section>
      </div>

      {tileMenu ? (
        <div className="opd-bill-tile-dropdown" ref={tileMenuRef} style={{ top: tileMenu.top, left: tileMenu.left }} role="menu">
          <button type="button" role="menuitem" onClick={() => openAdmissionFormPrint(false)}><IconPrinter />Form without Header</button>
          <button type="button" role="menuitem" onClick={() => openAdmissionFormPrint(true)}><IconPrinter />Form with Header</button>
        </div>
      ) : null}
    </div>
  );
};
